import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
} from "@mui/material";
import PopoverComponent from "../../components/PopoverComponent";
import { useNotifications } from "@toolpad/core/useNotifications";
import {
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridRowParams,
} from "@mui/x-data-grid";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../context/auth";
import { ProcessSectionForm } from "./components/process-section-form";
import { ProcessSectionDetails } from "./components/process-section-details";
import {
  ProcessSection,
  CreateProcessSectionRequest,
  UpdateProcessSectionRequest,
  fetchProcessSections,
  createProcessSection,
  updateProcessSection,
  deleteProcessSection,
} from "../../api/process-sections";

export default function ProcessSections() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [openForm, setOpenForm] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);
  const [selectedSection, setSelectedSection] = useState<ProcessSection | null>(
    null
  );
  const [editingSection, setEditingSection] = useState<ProcessSection | null>(
    null
  );
  const notifications = useNotifications();

  // Popover state for delete confirmation
  const [deletePopover, setDeletePopover] = useState<{
    open: boolean;
    anchorEl: HTMLElement | null;
    sectionId: number | null;
  }>({
    open: false,
    anchorEl: null,
    sectionId: null,
  });

  // Queries
  const {
    data: sections = [],
    isLoading,
    error,
    refetch: refetchSections,
  } = useQuery({
    queryKey: ["process-sections"],
    queryFn: () => fetchProcessSections(user?.token || ""),
    enabled: !!user?.token,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 3,
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: CreateProcessSectionRequest) =>
      createProcessSection(data, user?.token || ""),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["process-sections"] });
      setOpenForm(false);
      notifications.show(response.message || "بخش فرایند با موفقیت ایجاد شد", {
        severity: "success",
      });
    },
    onError: (error: Error) => {
      notifications.show(error.message || "خطا در ایجاد بخش فرایند", {
        severity: "error",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: UpdateProcessSectionRequest;
    }) => updateProcessSection(id, data, user?.token || ""),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["process-sections"] });
      setOpenForm(false);
      setEditingSection(null);
      notifications.show(
        response.message || "بخش فرایند با موفقیت بروزرسانی شد",
        { severity: "success" }
      );
    },
    onError: (error: Error) => {
      notifications.show(error.message || "خطا در بروزرسانی بخش فرایند", {
        severity: "error",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteProcessSection(id, user?.token || ""),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["process-sections"] });
      notifications.show(response.message || "بخش فرایند با موفقیت حذف شد", {
        severity: "success",
      });
    },
    onError: (error: Error) => {
      notifications.show(error.message || "خطا در حذف بخش فرایند", {
        severity: "error",
      });
    },
  });

  // Handlers
  const handleCreate = () => {
    setEditingSection(null);
    setOpenForm(true);
  };

  const handleEdit = (section: ProcessSection) => {
    setEditingSection(section);
    setOpenForm(true);
  };

  const handleView = (section: ProcessSection) => {
    setSelectedSection(section);
    setOpenDetails(true);
  };

  const handleDelete = (event: React.MouseEvent<HTMLElement>, id: number) => {
    setDeletePopover({
      open: true,
      anchorEl: event.currentTarget,
      sectionId: id,
    });
  };

  const handleDeleteConfirm = () => {
    if (deletePopover.sectionId) {
      deleteMutation.mutate(deletePopover.sectionId);
      setDeletePopover({
        open: false,
        anchorEl: null,
        sectionId: null,
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeletePopover({
      open: false,
      anchorEl: null,
      sectionId: null,
    });
  };

  const handleFormSubmit = (data: CreateProcessSectionRequest) => {
    if (editingSection) {
      updateMutation.mutate({ id: editingSection.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  // Columns
  const columns: GridColDef[] = [
    {
      field: "sectionNumber",
      headerName: "شماره بخش",
      width: 120,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "name",
      headerName: "نام بخش",
      width: 200,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "description",
      headerName: "توضیحات",
      width: 300,
      valueFormatter: (params: any) => {
        return params.value || "-";
      },
    },
    {
      field: "actions",
      type: "actions",
      headerName: "عملیات",
      width: 120,
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          icon={<ViewIcon />}
          label="مشاهده"
          onClick={() => handleView(params.row)}
        />,
        <GridActionsCellItem
          icon={<EditIcon />}
          label="ویرایش"
          onClick={() => handleEdit(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="حذف"
          onClick={(event) => handleDelete(event, params.row.id)}
        />,
      ],
    },
  ];

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">خطا در بارگذاری داده‌ها</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          disabled={createMutation.isPending}
        >
          بخش جدید
        </Button>
      </Box>

      <Paper sx={{ height: 600, width: "100%" }}>
        <DataGrid
          rows={sections}
          columns={columns}
          loading={isLoading}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
          disableRowSelectionOnClick
        />
      </Paper>

      {/* Form Dialog */}
      <Dialog
        open={openForm}
        onClose={() => setOpenForm(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingSection ? "ویرایش بخش فرایند" : "بخش جدید"}
        </DialogTitle>
        <DialogContent>
          <ProcessSectionForm
            section={editingSection}
            existingSections={sections}
            onSubmit={handleFormSubmit}
            loading={createMutation.isPending || updateMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Details Dialog */}
      <Dialog
        open={openDetails}
        onClose={() => setOpenDetails(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>جزئیات بخش فرایند</DialogTitle>
        <DialogContent>
          {selectedSection && (
            <ProcessSectionDetails section={selectedSection} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetails(false)}>بستن</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Popover */}
      <PopoverComponent
        id="delete-popover"
        open={deletePopover.open}
        anchorEl={deletePopover.anchorEl}
        handlePopoverClose={handleDeleteCancel}
        handler={handleDeleteConfirm}
        isLoading={deleteMutation.isPending}
        caption="آیا از حذف این بخش فرایند اطمینان دارید؟"
      />

      {/* Notifications handled via useNotifications */}
    </Box>
  );
}
